/**
 * The circuit breaker, on screen.
 *
 * Its own card rather than a line in the run panel, because it answers a
 * different question. The run panel answers "may I press this"; this answers
 * "should anyone be sending at all", and the answer is about the sending domain
 * as much as about this campaign.
 *
 * THREE STATES, NOT TWO. Clean, tripped, and waived - and waived must not look
 * clean. An acknowledgement is somebody's decision to keep going past a limit,
 * it expires, and while it is live the card says so and says until when. A card
 * that showed a green tick during a waiver would be hiding the one fact the
 * operator most needs to remember they created.
 *
 * The two rate blocks are `BreakerScope`, shared with the overview screen. The
 * rule about a rate that cannot yet be measured lives there, with the drawing
 * of it.
 */
import { useState } from "react";
import { AlertTriangle, Gauge, ShieldCheck, ShieldAlert } from "lucide-react";
import { Button } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { ackBreaker, clearBreakerAck, type BreakerView } from "../../../../lib/marketing";
import { rateText } from "../lib";
import { Note, Panel, dt } from "../shared";
import { Scope } from "./BreakerScope";

export function BreakerCard({
  campaignId,
  breaker,
  canManage,
  onChanged,
}: {
  campaignId: string;
  breaker: BreakerView;
  canManage: boolean;
  onChanged: () => void;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setFailed(false);
    try {
      await fn();
      onChanged();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const state = breaker.trip ? "tripped" : breaker.waived ? "waived" : "ok";
  const icon =
    state === "tripped" ? <ShieldAlert className="size-4" /> : state === "waived" ? <Gauge className="size-4" /> : <ShieldCheck className="size-4" />;

  return (
    <Panel title={t("marketing.breaker.title")} description={t("marketing.breaker.intro")} icon={icon}>
      {breaker.trip ? (
        <Note tone="bad" icon={<AlertTriangle className="size-4" />} className="mb-4">
          {t(`marketing.breaker.tripped.${breaker.trip.reason}`, {
            scope: t(`marketing.breaker.scope.${breaker.trip.scope}`),
            rate: rateText(breaker.trip.rate),
            limit: rateText(breaker.trip.limit),
          })}
        </Note>
      ) : null}

      {breaker.ack ? (
        <Note tone="warn" className="mb-4">
          {t(breaker.waived ? "marketing.breaker.waived" : "marketing.breaker.waiverOutgrown", {
            until: dt(breaker.ack.until),
            bounce: rateText(breaker.ack.bounceRate),
            complaint: rateText(breaker.ack.complaintRate),
          })}
        </Note>
      ) : null}

      <div className="space-y-4">
        <Scope
          title={t("marketing.breaker.scope.campaign")}
          rates={breaker.campaign}
          windowDays={breaker.windowDays}
          limits={breaker.limits}
        />
        <div className="border-t border-border pt-4">
          <Scope
            title={t("marketing.breaker.scope.domain")}
            rates={breaker.domain}
            windowDays={breaker.windowDays}
            limits={breaker.limits}
          />
        </div>
      </div>

      {/* The clear button follows the ACK's existence, not the waiver's. An
          acknowledgement that has stopped covering - because the rate got worse
          than what was accepted - is still a stored decision with the operator's
          name on it, and the first version of this card left it on screen as
          nothing at all: no note, no button, and no way to take it back before
          it expired on its own. */}
      {breaker.trip || breaker.ack ? (
        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border pt-4">
          {breaker.trip ? (
            <Button
              variant="outline"
              disabled={!canManage || busy}
              onClick={() => void act(() => ackBreaker(campaignId, 24))}
            >
              {t("marketing.breaker.ack")}
            </Button>
          ) : null}
          {breaker.ack ? (
            <Button variant="outline" disabled={!canManage || busy} onClick={() => void act(() => clearBreakerAck())}>
              {t("marketing.breaker.clear")}
            </Button>
          ) : null}
          <span className="crm-sub">{t("marketing.breaker.ackHint")}</span>
        </div>
      ) : null}

      {failed ? (
        <Note tone="warn" className="mt-3">
          {t("marketing.breaker.actionFailed")}
        </Note>
      ) : null}
    </Panel>
  );
}
